import { useEffect, useMemo, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { Form } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMagnifyingGlass,
  faPlus,
  faTrashCan,
  faUser,
  faUsers,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import swal from "sweetalert";
import { APISD } from "../../../../../../config/apisd";
import { APISM } from "../../../../../../config/apism";
import LoaderAction from "../../../../../Loader/LoaderHome";
import ToastSuccess from "../../../../../NotificationToast/ToastSuccess";

const cleanId = (value) => String(value ?? "");
const getUserId = () =>
  String(localStorage.getItem("id_admin") || "").replace(
    /[|&;$%@"<>()+,]/g,
    "",
  );

export default function WalasFormModal({
  context,
  dataUpdate,
  GetResponseData,
  onHide,
  ...modalProps
}) {
  const token = localStorage.getItem("token");
  const requestConfig = useMemo(
    () => ({
      headers: {
        Authorization: `${token}`,
        "Content-Type": "application/json",
      },
    }),
    [token],
  );
  const levelClasses = useMemo(
    () => dataUpdate?.classes || [],
    [dataUpdate?.classes],
  );
  const [loading, setLoading] = useState(false);
  const [loadingTeachers, setLoadingTeachers] = useState(false);
  const [teachers, setTeachers] = useState([]);
  const [semesterIds, setSemesterIds] = useState([]);
  const [selectedByClass, setSelectedByClass] = useState({});
  const [initialByClass, setInitialByClass] = useState({});
  const [searchByClass, setSearchByClass] = useState({});

  useEffect(() => {
    setSemesterIds(
      (context?.semesterList || []).map((item) => item?.id).filter(Boolean),
    );
    const initialSelection = levelClasses.reduce((result, item) => {
      result[cleanId(item?.ysb_kelas_id)] = (item?.teachers || [])
        .map((teacher) => cleanId(teacher?.ysb_teacher_id))
        .filter(Boolean);
      return result;
    }, {});
    setSelectedByClass(initialSelection);
    setInitialByClass(initialSelection);
  }, [context?.semesterList, levelClasses]);

  useEffect(() => {
    const loadTeachers = async () => {
      if (!context?.schoolId) return;
      setLoadingTeachers(true);
      try {
        const response = await APISM.get(
          `/api/access/teachers?page=1&limit=1000&ascending=1&search=&branch=${context.branchId}`,
          requestConfig,
        );
        setTeachers(
          (response?.data?.data || []).filter(
            (item) =>
              !item?.ysb_school_id || item?.ysb_school_id === context.schoolId,
          ),
        );
      } catch (error) {
        setTeachers([]);
        swal({
          title: "Gagal",
          text:
            error?.response?.data?.message ||
            "Gagal mengambil daftar wali kelas",
          icon: "error",
        });
      } finally {
        setLoadingTeachers(false);
      }
    };
    loadTeachers();
  }, [context?.branchId, context?.schoolId, requestConfig]);

  const toggleSemester = (id) =>
    setSemesterIds((current) =>
      current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id],
    );
  const toggleTeacher = (classId, teacherId) => {
    const classKey = cleanId(classId);
    const teacherKey = cleanId(teacherId);
    setSelectedByClass((current) => {
      const selected = current[classKey] || [];
      return {
        ...current,
        [classKey]: selected.includes(teacherKey)
          ? selected.filter((id) => id !== teacherKey)
          : [...selected, teacherKey],
      };
    });
  };

  const makePayload = (classItem, semesterId, teacherIds) => {
    const semester = (context?.semesterList || []).find(
      (item) => cleanId(item?.id) === cleanId(semesterId),
    );
    return {
      ysb_academic_year_id:
        semester?.ysb_academic_year_id ||
        semester?.academic_year_id ||
        context?.academicYearId ||
        context?.academicYear,
      ysb_semester_id: semesterId,
      ysb_branch_id: context?.branchId,
      ysb_school_id: context?.schoolId,
      ysb_grade_level_id:
        classItem?.ysb_grade_level_id ||
        classItem?.grade_level_id ||
        classItem?.ysb_level_id,
      ysb_kelas_id: classItem?.ysb_kelas_id,
      ysb_teacher_id: teacherIds,
      user_id: getUserId(),
    };
  };

  const refreshAndClose = async () => {
    onHide();
    await GetResponseData?.();
  };

  const deleteClassWalas = async (classItem) => {
    const assignments = (classItem?.teachers || []).filter(
      (teacher) => teacher?.assignment_id,
    );
    const total = assignments.length;

    if (!total) {
      swal({
        title: "Tidak Dapat Menghapus",
        text: "ID assignment wali kelas tidak ditemukan.",
        icon: "warning",
      });
      return;
    }

    const confirmed = await swal({
      title: `Hapus Walas Kelas ${classItem?.name_kelas || ""}?`,
      text: `Seluruh ${total} wali kelas pada kelas ini akan dihapus.`,
      icon: "warning",
      buttons: ["Batal", "Ya, Hapus"],
      dangerMode: true,
    });
    if (!confirmed) return;
    setLoading(true);
    try {
      await Promise.all(
        assignments.map((teacher) =>
          APISD.delete(
            `/api/walas-assignments/${encodeURIComponent(teacher.assignment_id)}`,
            requestConfig,
          ),
        ),
      );
      ToastSuccess.fire({
        icon: "success",
        title: "Seluruh wali kelas berhasil dihapus",
      });
      await refreshAndClose();
    } catch (error) {
      swal({
        title: "Gagal",
        text: error?.response?.data?.message || "Gagal menghapus wali kelas",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!semesterIds.length) return;
    setLoading(true);
    try {
      const requests = [];
      levelClasses.forEach((classItem) => {
        const classKey = cleanId(classItem?.ysb_kelas_id);
        const initialIds = initialByClass[classKey] || [];
        const selectedIds = selectedByClass[classKey] || [];
        const normalizedInitial = [...initialIds].sort().join(",");
        const normalizedSelected = [...selectedIds].sort().join(",");

        // Card kosong yang tidak disentuh dan card yang tidak berubah tidak
        // perlu mengirim request ke API.
        if (normalizedInitial === normalizedSelected) return;

        // Pengosongan walas dilakukan khusus melalui tombol trash.
        if (selectedIds.length === 0) return;

        const isNewAssignment = initialIds.length === 0;
        const endpoint = isNewAssignment
          ? "/api/walas-assignments/store"
          : "/api/walas-assignments/update";
        const method = isNewAssignment ? "post" : "put";

        semesterIds.forEach((semesterId) => {
          requests.push(
            APISD[method](
              endpoint,
              makePayload(classItem, semesterId, selectedIds),
              requestConfig,
            ),
          );
        });
      });

      if (requests.length === 0) {
        ToastSuccess.fire({
          icon: "info",
          title: "Tidak ada perubahan wali kelas",
        });
        onHide();
        return;
      }

      await Promise.all(requests);
      ToastSuccess.fire({
        icon: "success",
        title: "Wali kelas berhasil diperbarui",
      });
      await refreshAndClose();
    } catch (error) {
      swal({
        title: "Gagal",
        text: error?.response?.data?.message || "Gagal memperbarui wali kelas",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <LoaderAction />}
      <Modal
        {...modalProps}
        show
        size="xl"
        centered
        backdrop="static"
        keyboard={!loading}
        dialogClassName="walas-modal-dialog"
        contentClassName="walas-modal-content"
      >
        <Form onSubmit={handleSubmit}>
          <Modal.Header className="walas-modal-header">
            <div>
              <h5>
                Kelola Wali Kelas Tingkatan {dataUpdate?.name_level || "-"}
              </h5>
              <p>Atur wali kelas untuk seluruh kelas dalam tingkatan ini.</p>
            </div>
            <button type="button" onClick={onHide} disabled={loading}>
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </Modal.Header>
          <Modal.Body className="walas-modal-body">
            <section className="walas-info-grid">
              <div>
                <small>TAHUN AJARAN</small>
                <strong>{context?.academicYear || "-"}</strong>
              </div>
              <div>
                <small>CABANG</small>
                <strong>{context?.branchName || "-"}</strong>
              </div>
              <div>
                <small>SEKOLAH</small>
                <strong>{context?.schoolName || "-"}</strong>
              </div>
              <div>
                <small>TINGKATAN</small>
                <strong>{dataUpdate?.name_level || "-"}</strong>
              </div>
            </section>
            <div className="walas-field">
              <label>
                Pilih Semester <span>*</span>
              </label>
              <div className="walas-semesters">
                {(context?.semesterList || []).map((semester) => (
                  <label key={semester?.id}>
                    <input
                      type="checkbox"
                      checked={semesterIds.includes(semester?.id)}
                      onChange={() => toggleSemester(semester?.id)}
                    />
                    <span>Semester {semester?.semester}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="walas-class-grid">
              {levelClasses.map((classItem) => {
                const classKey = cleanId(classItem?.ysb_kelas_id);
                const selectedIds = selectedByClass[classKey] || [];
                const keyword = (searchByClass[classKey] || "")
                  .trim()
                  .toLowerCase();
                const displayed = teachers.filter(
                  (teacher) =>
                    !keyword ||
                    [
                      teacher?.full_name,
                      teacher?.nik_ysb,
                      teacher?.nip_ypi,
                    ].some((value) =>
                      String(value || "")
                        .toLowerCase()
                        .includes(keyword),
                    ),
                );
                return (
                  <section className="walas-class-card" key={classKey}>
                    <div className="walas-class-head">
                      <div>
                        <small>KELAS</small>
                        <h6>{classItem?.name_kelas || "-"}</h6>
                      </div>
                      <div className="walas-class-actions">
                        <span>{selectedIds.length} walas</span>
                        <button
                          type="button"
                          className="walas-trash"
                          title="Hapus seluruh walas kelas ini"
                          disabled={
                            !(classItem?.teachers || []).length || loading
                          }
                          onClick={() => deleteClassWalas(classItem)}
                        >
                          <FontAwesomeIcon icon={faTrashCan} />
                        </button>
                      </div>
                    </div>
                    {!!selectedIds.length && (
                      <div className="walas-chips">
                        {selectedIds.map((teacherId) => {
                          const teacher = teachers.find(
                            (item) => cleanId(item?.id) === teacherId,
                          );
                          const fallback = (classItem?.teachers || []).find(
                            (item) =>
                              cleanId(item?.ysb_teacher_id) === teacherId,
                          );
                          return (
                            <button
                              type="button"
                              key={teacherId}
                              onClick={() => toggleTeacher(classKey, teacherId)}
                            >
                              <FontAwesomeIcon icon={faUser} />{" "}
                              {teacher?.full_name ||
                                fallback?.full_name ||
                                "Wali Kelas"}{" "}
                              <FontAwesomeIcon icon={faXmark} />
                            </button>
                          );
                        })}
                      </div>
                    )}
                    <div className="walas-search">
                      <FontAwesomeIcon icon={faMagnifyingGlass} />
                      <input
                        value={searchByClass[classKey] || ""}
                        onChange={(e) =>
                          setSearchByClass((old) => ({
                            ...old,
                            [classKey]: e.target.value,
                          }))
                        }
                        placeholder="Cari nama wali kelas atau NIK..."
                      />
                    </div>
                    <div className="walas-teacher-list">
                      {loadingTeachers ? (
                        <div className="walas-list-info" style={{fontSize: "13px", color: "#3f5bea", fontWeight: 600 }}>
                          Memuat daftar wali kelas...
                        </div>
                      ) : !displayed.length ? (
                        <div className="walas-list-info">
                          Wali kelas tidak ditemukan
                        </div>
                      ) : (
                        displayed.map((teacher) => {
                          const chosen = selectedIds.includes(
                            cleanId(teacher?.id),
                          );
                          return (
                            <div className="walas-teacher" key={teacher?.id}>
                              <span className="walas-avatar">
                                <FontAwesomeIcon icon={faUser} />
                              </span>
                              <div>
                                <strong>{teacher?.full_name || "-"}</strong>
                                <small>
                                  {teacher?.nik_ysb || teacher?.nip_ypi || "-"}
                                </small>
                              </div>
                              <button
                                type="button"
                                className={chosen ? "chosen" : ""}
                                onClick={() =>
                                  toggleTeacher(classKey, teacher?.id)
                                }
                              >
                                <FontAwesomeIcon
                                  icon={chosen ? faXmark : faPlus}
                                />{" "}
                                {chosen ? "Hapus" : "Pilih"}
                              </button>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </section>
                );
              })}
            </div>
          </Modal.Body>
          <Modal.Footer className="walas-modal-footer">
            <p>
              Perubahan diterapkan ke{" "}
              <strong>{levelClasses.length} kelas</strong>.
            </p>
            <div>
              <button type="button" className="walas-cancel" onClick={onHide}>
                Batal
              </button>
              <button
                type="submit"
                className="walas-submit"
                disabled={loading || !semesterIds.length}
              >
                <FontAwesomeIcon icon={faUsers} />{" "}
                {loading ? "Menyimpan..." : "Simpan Perubahan"}
              </button>
            </div>
          </Modal.Footer>
        </Form>
      </Modal>
      <style>{`
      .walas-modal-dialog{max-width:1180px}.walas-modal-content{border:0;border-radius:18px;overflow:hidden;box-shadow:0 24px 70px rgba(23,43,77,.2)}.walas-modal-header{display:flex;align-items:center;justify-content:space-between;padding:21px 26px;border-bottom:1px solid #e4e9f3}.walas-modal-header h5{margin:0;font-size:20px;font-weight:800;color:#14213d}.walas-modal-header p{margin:4px 0 0;color:#7a899f;font-size:13px}.walas-modal-header>button{border:0;background:#f1f5f9;width:36px;height:36px;border-radius:10px;color:#475569}.walas-modal-body{padding:24px;max-height:70vh;overflow-y:auto;background:#f8fafc}.walas-info-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:14px;padding:16px 18px;border:1px solid #cddcff;border-radius:12px;background:#f2f6ff;margin-bottom:20px}.walas-info-grid div{display:flex;flex-direction:column;gap:4px}.walas-info-grid small,.walas-class-head small{font-size:10px;font-weight:800;color:#8492a9;letter-spacing:.5px}.walas-info-grid strong{font-size:13px;color:#40516e}.walas-field{margin-bottom:20px}.walas-field>label{display:block;font-weight:700;color:#26334d;margin-bottom:8px}.walas-field>label span{color:#ef4444}.walas-semesters{display:grid;grid-template-columns:repeat(2,1fr);gap:10px}.walas-semesters label{display:flex;align-items:center;gap:10px;border:1px solid #d3ddef;border-radius:10px;padding:12px;background:#fff;font-weight:700}.walas-semesters input{width:17px;height:17px;accent-color:#4965ec}.walas-class-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:16px}.walas-class-card:last-child:nth-child(odd){grid-column:1/-1}.walas-class-card{min-width:0;padding:18px;border:1px solid #dce4f0;border-radius:14px;background:#fff;box-shadow:0 4px 14px rgba(61,82,120,.05)}.walas-class-head{display:flex;align-items:center;justify-content:space-between;margin-bottom:14px}.walas-class-head h6{margin:2px 0 0;font-size:18px;font-weight:800;color:#1d2c4b;text-transform:capitalize}.walas-class-actions{display:flex;align-items:center;gap:9px}.walas-class-actions>span{padding:5px 9px;border-radius:14px;background:#edf2ff;color:#3454ca;font-size:11px;font-weight:800}.walas-trash{width:34px;height:34px;border:1px solid #fecaca;border-radius:9px;background:#fff5f5;color:#dc2626}.walas-trash:disabled{opacity:.35}.walas-chips{display:flex;gap:7px;flex-wrap:wrap;margin-bottom:11px}.walas-chips button{border:1px solid #c8d7fb;border-radius:18px;background:#eff5ff;color:#3152a4;padding:7px 9px;font-size:11px;font-weight:700}.walas-search{height:42px;display:flex;align-items:center;gap:9px;border:1px solid #ccd7e8;border-radius:9px;padding:0 12px;color:#8ca0bc}.walas-search input{border:0;outline:0;width:100%;min-width:0;font-size:13px}.walas-teacher-list{height:220px;overflow-y:auto;border:1px solid #e2e8f0;border-radius:9px;margin-top:9px}.walas-teacher{display:grid;grid-template-columns:36px minmax(0,1fr) auto;align-items:center;gap:9px;padding:9px 10px;border-bottom:1px solid #e8edf4}.walas-avatar{width:34px;height:34px;border-radius:50%;display:grid;place-items:center;background:#dce8ff;color:#4164df}.walas-teacher>div{display:flex;flex-direction:column;min-width:0}.walas-teacher strong{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size:12px}.walas-teacher small{font-size:11px;color:#7585a0}.walas-teacher button{border:0;border-radius:8px;background:#edf3ff;color:#3158d4;padding:7px 9px;font-size:11px;font-weight:700}.walas-teacher button.chosen{background:#fff0f1;color:#dc3545}.walas-list-info{text-align:center;padding:28px;color:#7b8aa3}.walas-modal-footer{display:flex;align-items:center;justify-content:space-between;padding:16px 24px}.walas-modal-footer p{margin:0;color:#72819a;font-size:13px}.walas-modal-footer>div{display:flex;gap:9px}.walas-modal-footer button{height:42px;padding:0 16px;border-radius:10px;font-weight:800}.walas-cancel{border:1px solid #d2dbea;background:#fff}.walas-submit{border:0;background:#4561e9;color:#fff}.walas-submit:disabled{opacity:.55}@media(max-width:850px){.walas-class-grid{grid-template-columns:1fr}.walas-info-grid{grid-template-columns:repeat(2,1fr)}}@media(max-width:600px){.walas-modal-body{padding:16px}.walas-info-grid,.walas-semesters{grid-template-columns:1fr}.walas-modal-footer{align-items:stretch;flex-direction:column;gap:11px}.walas-modal-footer>div{display:grid;grid-template-columns:1fr 1fr}}`}</style>
      <style>{`
        .walas-modal-dialog {
          width: calc(100% - 32px);
          margin: 16px auto;
        }

        .walas-modal-content {
          max-height: calc(100vh - 32px);
        }

        .walas-modal-content form {
          display: flex;
          min-height: 0;
          max-height: calc(100vh - 32px);
          flex-direction: column;
        }

        .walas-modal-header {
          flex: 0 0 auto;
          padding: 20px 24px;
          background: #ffffff;
        }

        .walas-modal-header > div {
          min-width: 0;
          padding-right: 16px;
        }

        .walas-modal-header > button {
          display: grid;
          flex: 0 0 38px;
          width: 38px;
          height: 38px;
          place-items: center;
          border: 1px solid #e2e8f0;
          cursor: pointer;
          transition: 0.2s ease;
        }

        .walas-modal-header > button:hover:not(:disabled) {
          border-color: #cbd5e1;
          background: #e9eef5;
          color: #1e293b;
        }

        .walas-modal-body {
          min-height: 0;
          max-height: none;
          padding: 22px 24px 24px;
          background: #f7f9fc;
        }

        .walas-info-grid {
          gap: 0;
          padding: 16px 18px;
          background: #f1f5ff;
        }

        .walas-info-grid > div {
          min-width: 0;
          padding: 0 16px;
          border-right: 1px solid #d9e3f7;
        }

        .walas-info-grid > div:first-child {
          padding-left: 0;
        }

        .walas-info-grid > div:last-child {
          padding-right: 0;
          border-right: 0;
        }

        .walas-info-grid strong {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .walas-field > label {
          font-size: 13px;
          font-weight: 800;
        }

        .walas-semesters label {
          margin: 0;
          padding: 12px 14px;
          color: #35445e;
          font-size: 13px;
          cursor: pointer;
          transition: 0.2s ease;
        }

        .walas-semesters label:hover {
          border-color: #9fb6f4;
          background: #f8faff;
        }

        .walas-semesters input {
          width: 18px;
          height: 18px;
          margin: 0;
          cursor: pointer;
        }

        .walas-class-grid {
          align-items: start;
        }

        .walas-class-card {
          padding: 17px;
        }

        .walas-class-head {
          gap: 12px;
          margin-bottom: 13px;
          padding-bottom: 12px;
          border-bottom: 1px solid #edf1f6;
        }

        .walas-class-head h6 {
          font-size: 17px;
        }

        .walas-class-actions {
          flex: 0 0 auto;
          gap: 8px;
        }

        .walas-class-actions > span,
        .walas-chips button {
          border-radius: 999px;
        }

        .walas-trash {
          display: grid;
          place-items: center;
          cursor: pointer;
          transition: 0.2s ease;
        }

        .walas-trash:hover:not(:disabled) {
          border-color: #fca5a5;
          background: #fee2e2;
        }

        .walas-trash:disabled {
          cursor: not-allowed;
        }

        .walas-chips button {
          max-width: 100%;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          cursor: pointer;
        }

        .walas-search {
          background: #ffffff;
          transition: 0.2s ease;
        }

        .walas-search:focus-within {
          border-color: #6f8cf0;
          box-shadow: 0 0 0 3px rgba(69, 97, 233, 0.1);
        }

        .walas-search input {
          background: transparent;
          color: #26334d;
        }

        .walas-search input::placeholder {
          color: #9aa8ba;
        }

        .walas-teacher-list {
          height: 224px;
          background: #ffffff;
        }

        .walas-teacher-list::-webkit-scrollbar {
          width: 7px;
        }

        .walas-teacher-list::-webkit-scrollbar-thumb {
          border: 2px solid #ffffff;
          border-radius: 999px;
          background: #cbd5e1;
        }

        .walas-teacher {
          transition: background 0.15s ease;
        }

        .walas-teacher:last-child {
          border-bottom: 0;
        }

        .walas-teacher:hover {
          background: #f8faff;
        }

        .walas-teacher strong {
          color: #273651;
        }

        .walas-teacher button {
          min-width: 68px;
          cursor: pointer;
          transition: 0.2s ease;
        }

        .walas-teacher button:hover {
          background: #dfe9ff;
        }

        .walas-teacher button.chosen:hover {
          background: #ffe1e4;
        }

        .walas-modal-footer {
          flex: 0 0 auto;
          gap: 16px;
          padding: 15px 24px;
          border-top: 1px solid #e4e9f3;
          background: #ffffff;
          box-shadow: 0 -5px 16px rgba(30, 51, 88, 0.04);
        }

        .walas-modal-footer > div {
          flex: 0 0 auto;
        }

        .walas-modal-footer button {
          font-size: 13px;
          cursor: pointer;
          transition: 0.2s ease;
        }

        .walas-cancel {
          color: #52617a;
        }

        .walas-cancel:hover {
          background: #f8fafc;
        }

        .walas-submit {
          box-shadow: 0 5px 12px rgba(69, 97, 233, 0.22);
        }

        .walas-submit:hover:not(:disabled) {
          background: #3853d4;
        }

        .walas-submit:disabled {
          cursor: not-allowed;
          box-shadow: none;
        }

        @media (max-width: 850px) {
          .walas-info-grid {
            row-gap: 14px;
          }

          .walas-info-grid > div,
          .walas-info-grid > div:first-child,
          .walas-info-grid > div:last-child {
            padding: 0 12px;
            border-right: 0;
          }

          .walas-info-grid > div:nth-child(odd) {
            padding-left: 0;
            border-right: 1px solid #d9e3f7;
          }

          .walas-class-card:last-child:nth-child(odd) {
            grid-column: auto;
          }
        }

        @media (max-width: 600px) {
          .walas-modal-dialog {
            width: 100%;
            min-height: 100%;
            margin: 0;
          }

          .walas-modal-content,
          .walas-modal-content form {
            min-height: 100vh;
            max-height: 100vh;
            border-radius: 0;
          }

          .walas-modal-header {
            align-items: flex-start;
            padding: 16px;
          }

          .walas-modal-header h5 {
            font-size: 17px;
          }

          .walas-modal-header p {
            font-size: 12px;
          }

          .walas-modal-body {
            padding: 16px;
          }

          .walas-info-grid {
            gap: 11px;
            padding: 14px;
          }

          .walas-info-grid > div,
          .walas-info-grid > div:first-child,
          .walas-info-grid > div:last-child,
          .walas-info-grid > div:nth-child(odd) {
            padding: 0 0 10px;
            border-right: 0;
            border-bottom: 1px solid #d9e3f7;
          }

          .walas-info-grid > div:last-child {
            padding-bottom: 0;
            border-bottom: 0;
          }

          .walas-class-card {
            padding: 14px;
          }

          .walas-class-head {
            align-items: flex-start;
          }

          .walas-teacher {
            grid-template-columns: 34px minmax(0, 1fr) auto;
            padding: 9px 8px;
          }

          .walas-teacher button {
            min-width: 38px;
            padding: 7px;
            font-size: 0;
          }

          .walas-teacher button svg {
            font-size: 12px;
          }

          .walas-modal-footer {
            gap: 10px;
            padding: 13px 16px 16px;
          }

          .walas-modal-footer button {
            width: 100%;
            padding: 0 10px;
          }
        }
      `}</style>
    </>
  );
}
